
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import AdminHeader from '../../../components/feature/AdminHeader';

interface KBMNewsItem {
  id: string;
  title: string;
  content: string;
  author: string;
  published_date: string;
  type: string;
  display_order: number;
  files?: { id: string; file_url: string; file_type: string }[];
}

export default function KBMNewsAdmin() {
  const [newsList, setNewsList] = useState<KBMNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '킹덤브릿지선교회',
    published_date: new Date().toISOString().split('T')[0],
    display_order: 0,
  });

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('kbm_insights')
        .select('*')
        .eq('type', 'insight')
        .order('display_order', { ascending: false });

      if (error) throw error;

      const withFiles = await Promise.all(
        (data || []).map(async (item) => {
          const { data: filesData } = await supabase
            .from('kbm_insights_files')
            .select('*')
            .eq('insight_id', item.id);
          return { ...item, files: filesData || [] };
        })
      );

      setNewsList(withFiles);
    } catch (err) {
      console.error(err);
      showMessage('데이터를 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const combined = [...selectedFiles, ...newFiles].slice(0, 10);
    if (combined.length < selectedFiles.length + newFiles.length) {
      showMessage('최대 10장까지만 선택할 수 있습니다.', 'error');
    }
    setSelectedFiles(combined);
    const urls = combined.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    const updated = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updated);
    const urls = updated.map((f) => URL.createObjectURL(f));
    setPreviewUrls(urls);
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFiles = async (insightId: string) => {
    const total = selectedFiles.length;
    for (let i = 0; i < total; i++) {
      const file = selectedFiles[i];
      const ext = file.name.split('.').pop();
      const fileName = `${insightId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
      const fileType = file.type.includes('pdf') ? 'pdf' : 'image';

      const { error: uploadError } = await supabase.storage
        .from('kbm-insights')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('kbm-insights')
        .getPublicUrl(fileName);

      await supabase.from('kbm_insights_files').insert({
        insight_id: insightId,
        file_url: urlData.publicUrl,
        file_type: fileType,
      });

      setUploadProgress(Math.round(((i + 1) / total) * 100));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setUploadProgress(0);

    try {
      const payload = { ...formData, type: 'insight' };

      if (editingId) {
        const { error } = await supabase
          .from('kbm_insights')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
        if (selectedFiles.length > 0) await uploadFiles(editingId);
        showMessage('수정되었습니다.', 'success');
        setEditingId(null);
      } else {
        const { data, error } = await supabase
          .from('kbm_insights')
          .insert([payload])
          .select()
          .single();
        if (error) throw error;
        if (selectedFiles.length > 0 && data) await uploadFiles(data.id);
        showMessage('소식이 등록되었습니다.', 'success');
      }

      resetForm();
      fetchNews();
    } catch (err) {
      console.error(err);
      showMessage('오류가 발생했습니다.', 'error');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      author: '킹덤브릿지선교회',
      published_date: new Date().toISOString().split('T')[0],
      display_order: 0,
    });
    clearFiles();
    setEditingId(null);
  };

  const handleEdit = (item: KBMNewsItem) => {
    setFormData({
      title: item.title,
      content: item.content,
      author: item.author,
      published_date: item.published_date,
      display_order: item.display_order,
    });
    setEditingId(item.id);
    clearFiles();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('kbm_insights').delete().eq('id', id);
      if (error) throw error;
      showMessage('삭제되었습니다.', 'success');
      setDeleteConfirmId(null);
      fetchNews();
    } catch (err) {
      console.error(err);
      showMessage('삭제에 실패했습니다.', 'error');
    }
  };

  const handleDeleteFile = async (fileId: string, fileUrl: string) => {
    try {
      const filePath = fileUrl.split('/kbm-insights/')[1];
      if (filePath) await supabase.storage.from('kbm-insights').remove([filePath]);
      await supabase.from('kbm_insights_files').delete().eq('id', fileId);
      showMessage('파일이 삭제되었습니다.', 'success');
      fetchNews();
    } catch (err) {
      console.error(err);
      showMessage('파일 삭제에 실패했습니다.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AdminHeader title="킹덤브릿지 소식 관리" subtitle="킹덤브릿지선교회의 최신 소식을 관리합니다" />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Toast Message */}
        {message && (
          <div
            className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
              message.type === 'success'
                ? 'bg-[#2F4F4F] text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <i className={message.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
              {message.text}
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 flex items-center justify-center bg-[#2F4F4F] rounded-lg">
              <i className={`${editingId ? 'ri-edit-line' : 'ri-add-line'} text-white text-lg`}></i>
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {editingId ? '소식 수정' : '새 소식 등록'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F] outline-none text-sm transition-all"
                placeholder="소식 제목을 입력하세요"
                required
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                내용 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, content: e.target.value });
                  }
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F] outline-none text-sm transition-all resize-none"
                rows={5}
                placeholder="소식 내용을 입력하세요"
                required
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length} / 500</p>
            </div>

            {/* Author & Date */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  작성자 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F] outline-none text-sm transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  발행일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.published_date}
                  onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F] outline-none text-sm transition-all"
                  required
                />
              </div>
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                표시 순서
                <span className="ml-2 text-xs font-normal text-gray-400">(숫자가 클수록 먼저 표시)</span>
              </label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                className="w-full sm:w-40 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F] outline-none text-sm transition-all"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                이미지 업로드
                <span className="ml-2 text-xs font-normal text-gray-400">(최대 10장)</span>
              </label>

              <div
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#2F4F4F]/40 hover:bg-[#2F4F4F]/5 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-2">
                  <i className="ri-image-add-line text-xl text-gray-500"></i>
                </div>
                <p className="text-sm text-gray-500">클릭하여 이미지 선택</p>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, PDF 지원</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                multiple
                accept="image/*,application/pdf"
                className="hidden"
              />

              {/* Preview Grid */}
              {previewUrls.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">{selectedFiles.length}장 선택됨</span>
                    <button
                      type="button"
                      onClick={clearFiles}
                      className="text-xs text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      전체 삭제
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {previewUrls.map((url, idx) => (
                      <div key={idx} className="relative group aspect-square">
                        <img
                          src={url}
                          alt={`미리보기 ${idx + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {submitting && uploadProgress > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>업로드 중...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-[#2F4F4F] h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 bg-[#2F4F4F] text-white rounded-xl hover:bg-[#1a3333] transition-colors font-medium text-sm whitespace-nowrap disabled:opacity-60 cursor-pointer"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    처리 중...
                  </>
                ) : (
                  <>
                    <i className={editingId ? 'ri-save-line' : 'ri-send-plane-line'}></i>
                    {editingId ? '수정 완료' : '소식 등록'}
                  </>
                )}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                  취소
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg">
                <i className="ri-list-check text-emerald-600 text-lg"></i>
              </div>
              <h2 className="text-lg font-bold text-gray-900">등록된 소식</h2>
              <span className="px-2.5 py-0.5 bg-[#2F4F4F]/10 text-[#2F4F4F] text-xs font-semibold rounded-full">
                {newsList.length}건
              </span>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-[#2F4F4F] rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-3 text-sm">불러오는 중...</p>
            </div>
          ) : newsList.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-megaphone-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 font-medium">등록된 소식이 없습니다</p>
              <p className="text-gray-400 text-sm mt-1">위 양식으로 첫 소식을 등록해보세요</p>
            </div>
          ) : (
            <div className="space-y-4">
              {newsList.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-all duration-200 bg-gray-50/50"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          킹덤브릿지 소식
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {item.published_date}
                        </span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <i className="ri-sort-asc"></i>
                          순서 {item.display_order}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-1 truncate">{item.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">{item.content}</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {item.author}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(item)}
                        className="w-9 h-9 flex items-center justify-center bg-[#2F4F4F]/10 text-[#2F4F4F] rounded-lg hover:bg-[#2F4F4F]/20 transition-colors cursor-pointer"
                        title="수정"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(item.id)}
                        className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                        title="삭제"
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    </div>
                  </div>

                  {/* Attached Files */}
                  {item.files && item.files.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                        <i className="ri-attachment-line"></i>
                        첨부 파일 {item.files.length}개
                      </p>
                      <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                        {item.files.map((file) => (
                          <div key={file.id} className="relative group aspect-square">
                            {file.file_type === 'pdf' ? (
                              <a
                                href={file.file_url}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="flex items-center justify-center w-full h-full bg-red-50 rounded-lg border border-red-100 hover:bg-red-100 transition-colors"
                              >
                                <i className="ri-file-pdf-line text-xl text-red-500"></i>
                              </a>
                            ) : (
                              <img
                                src={file.file_url}
                                alt="첨부 이미지"
                                className="w-full h-full object-cover rounded-lg"
                              />
                            )}
                            <button
                              onClick={() => handleDeleteFile(file.id, file.file_url)}
                              className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">소식 삭제</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              이 소식을 삭제하면 첨부 파일도 함께 삭제됩니다.<br />정말 삭제하시겠습니까?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium text-sm hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
